import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBridgeData } from '@/vendor/coti-wallet-plugin';
import { useBridgeAdmin } from '@/hooks/useBridgeAdmin';
import { useDashboardAccess } from '@/hooks/useDashboardAccess';
import { useBandOracleStatus } from '@/hooks/useBandOracleStatus';
import { usePodOracleStatus } from '@/hooks/usePodOracleStatus';
import { OracleStatusCard } from '@/components/BandOracleStatusCard';
import { useCotiPluginContext } from '@/context/PrivacyBridgeContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel
} from '@/components/ui/dropdown-menu';
import { AlertCircle, ArrowLeftRight, Settings, DollarSign, Wallet as WalletIcon, RefreshCw, Pause, Play, Users, BarChart2, ExternalLink, Ban, CircleCheck } from 'lucide-react';
import { formatAmountLimitDisplay } from '@/lib/utils';
import { CONTRACT_ADDRESSES, SUPPORTED_TOKENS } from '@/contracts/config';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ConnectWalletGuard from '@/components/ConnectWalletGuard';
import AdminActionModal, { AdminActionType, CurrentFees } from '@/components/modals/AdminActionModal';
import OperatorsModal from '@/components/modals/OperatorsModal';
import { formatAdminError } from '@/lib/formatAdminError';
import { getExplorerBaseUrlForChain } from '@/chains';
import { useBridgeStatus, BridgeData, BridgeStatus, getChainConfig } from '@/vendor/coti-wallet-plugin';

const bridgeStatusLabel = (status: BridgeStatus): string => {
  switch (status) {
    case 'paused':
      return 'Paused';
    case 'withdraw-only':
      return 'Deposits Disabled';
    default:
      return 'Active';
  }
};

const bridgeStatusDotClass = (status: BridgeStatus): string => {
  switch (status) {
    case 'paused':
      return 'bg-red-500';
    case 'withdraw-only':
      return 'bg-orange-500';
    default:
      return 'bg-green-500';
  }
};

interface BridgeItemProps {
  bridge: BridgeData;
  isPod: boolean;
  chainId: number;
  getExplorerUrl: (address: string) => string;
  publicTokenAddress: string | null;
  privateTokenAddress: string | null;
  openModal: (
    actionType: AdminActionType,
    bridgeName: string,
    bridgeAddress: string,
    isNativeBridge: boolean,
    tokenDecimals?: number,
    tokenSymbol?: string,
    tokenAddress?: string
  ) => void;
  setOperatorsModal: (state: { isOpen: boolean; bridgeName: string; bridgeAddress: string; isNativeBridge: boolean }) => void;
  accessStatus: 'admin' | 'operator' | 'loading' | 'denied' | 'disconnected';
  feeSymbol: string;
}

const BridgeFeesDetail = ({ bridge }: { bridge: BridgeData }) => (
  <div className="text-xs text-muted-foreground space-y-0.5 min-w-0 max-w-full break-words">
    <div className="font-semibold text-foreground/70">Fees{bridge.feeTokenSymbol ? ` (${bridge.feeTokenSymbol})` : ''}</div>
    <div>Dep: {bridge.depositFixedFee} + {bridge.depositPercentageBps}bps (max {bridge.depositMaxFee})</div>
    <div>W/d: {bridge.withdrawFixedFee} + {bridge.withdrawPercentageBps}bps (max {bridge.withdrawMaxFee})</div>
    {bridge.minDepositAmount !== 'N/A' && (
      <>
        <div>
          Dep limits: {formatAmountLimitDisplay(bridge.minDepositAmount)} –{" "}
          {formatAmountLimitDisplay(bridge.maxDepositAmount)}
        </div>
        <div>
          W/d limits: {formatAmountLimitDisplay(bridge.minWithdrawAmount)} –{" "}
          {formatAmountLimitDisplay(bridge.maxWithdrawAmount)}
        </div>
      </>
    )}
  </div>
);

const BridgeActionsMenu = ({
  bridge,
  status,
  isNativeBridge,
  isPod,
  publicTokenAddress,
  chainId,
  openModal,
  setOperatorsModal,
  navigate,
  accessStatus,
  feeSymbol,
}: {
  bridge: BridgeData;
  status: string;
  isNativeBridge: boolean;
  isPod: boolean;
  publicTokenAddress: string | null;
  chainId: number;
  openModal: BridgeItemProps['openModal'];
  setOperatorsModal: BridgeItemProps['setOperatorsModal'];
  navigate: ReturnType<typeof useNavigate>;
  accessStatus: BridgeItemProps['accessStatus'];
  feeSymbol: string;
}) => {
  // PoD: hide factory-admin actions for operator-only wallets. COTI keeps full menu.
  const showAdminActions = !isPod || accessStatus === 'admin';

  return (
  <div className="flex items-center gap-1">
    <Button
      variant="ghost"
      size="sm"
      className="h-11 w-11 p-0"
      title="View Statistics"
      aria-label="View Statistics"
      onClick={() => navigate(`/backoffice/bridge/${bridge.bridgeAddress}/statistics`, {
        state: {
          bridgeName: bridge.bridgeName,
          tokenSymbol: bridge.publicToken,
          tokenDecimals: bridge.tokenDecimals,
          chainId,
        },
      })}
    >
      <BarChart2 className="h-4 w-4" />
    </Button>
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="h-11 w-11 p-0" aria-label="Bridge admin actions">
          <Settings className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="glass w-56">
        <DropdownMenuLabel>Operator Actions</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => openModal('setDepositFee', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge, bridge.tokenDecimals, bridge.publicToken)} className="cursor-pointer">
          <DollarSign className="mr-2 h-4 w-4" />Set Deposit Fee
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => openModal('setWithdrawFee', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge, bridge.tokenDecimals, bridge.publicToken)} className="cursor-pointer">
          <DollarSign className="mr-2 h-4 w-4" />Set Withdraw Fee
        </DropdownMenuItem>
        {isPod && (
          bridge.isDepositEnabled ? (
            <DropdownMenuItem onClick={() => openModal('disableDeposits', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge)} className="cursor-pointer">
              <Ban className="mr-2 h-4 w-4" />Disable Deposits
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => openModal('enableDeposits', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge)} className="cursor-pointer">
              <CircleCheck className="mr-2 h-4 w-4" />Enable Deposits
            </DropdownMenuItem>
          )
        )}
        {showAdminActions && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel>Admin Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => openModal('setLimits', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge, bridge.tokenDecimals, bridge.publicToken)} className="cursor-pointer">
              <ArrowLeftRight className="mr-2 h-4 w-4" />Set Limits
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openModal('rescueCoti', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge, 18, isPod ? feeSymbol : 'COTI')} className="cursor-pointer">
              <WalletIcon className="mr-2 h-4 w-4" />Rescue {isPod ? feeSymbol : 'COTI'} Fees
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => openModal('rescueNative', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge, bridge.tokenDecimals, bridge.publicToken, publicTokenAddress || '')} className="cursor-pointer">
              <WalletIcon className="mr-2 h-4 w-4" />Rescue {bridge.publicToken}
            </DropdownMenuItem>
            {status === 'paused' ? (
              <DropdownMenuItem onClick={() => openModal('unpause', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge)} className="cursor-pointer">
                <Play className="mr-2 h-4 w-4" />Unpause Contract
              </DropdownMenuItem>
            ) : (
              <DropdownMenuItem onClick={() => openModal('pause', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge)} className="cursor-pointer">
                <Pause className="mr-2 h-4 w-4" />Pause Contract
              </DropdownMenuItem>
            )}
            {!isPod && (
              bridge.isDepositEnabled ? (
                <DropdownMenuItem onClick={() => openModal('disableDeposits', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge)} className="cursor-pointer">
                  <Ban className="mr-2 h-4 w-4" />Disable Deposits
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem onClick={() => openModal('enableDeposits', bridge.bridgeName, bridge.bridgeAddress, isNativeBridge)} className="cursor-pointer">
                  <CircleCheck className="mr-2 h-4 w-4" />Enable Deposits
                </DropdownMenuItem>
              )
            )}
            <DropdownMenuItem onClick={() => setOperatorsModal({ isOpen: true, bridgeName: bridge.bridgeName, bridgeAddress: bridge.bridgeAddress, isNativeBridge })} className="cursor-pointer">
              <Users className="mr-2 h-4 w-4" />Set Operators
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  </div>
  );
};

const BridgeCard = ({ bridge, isPod, chainId, getExplorerUrl, publicTokenAddress, privateTokenAddress, openModal, setOperatorsModal, accessStatus, feeSymbol }: BridgeItemProps) => {
  const status = useBridgeStatus(bridge);
  // PoD "native" portals (ETH/AVAX) wrap to WETH/WAVAX — collateral is ERC-20.
  // Keep isNativeBridge false so rescue uses rescueERC20(underlying), not rescueNative.
  const isNativeBridge = isPod ? false : bridge.publicToken === 'COTI';
  const navigate = useNavigate();

  const balance = bridge.bridgeBalance !== 'Error'
    ? parseFloat(bridge.bridgeBalance).toFixed(6)
    : bridge.bridgeBalance;
  const fees = bridge.accumulatedCotiFees !== 'Error'
    ? parseFloat(bridge.accumulatedCotiFees).toFixed(6)
    : bridge.accumulatedCotiFees;

  return (
    <Card className="glass border border-border">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img
              src={bridge.publicTokenIcon}
              alt={bridge.publicToken}
              className="w-10 h-10 rounded-full shrink-0"
              onError={(e) => { e.currentTarget.style.display = 'none'; }}
            />
            <div className="min-w-0">
              <a
                href={getExplorerUrl(bridge.bridgeAddress)}
                target="_blank"
                rel="noopener noreferrer"
                className="text-base font-semibold text-primary hover:underline truncate block"
              >
                {bridge.publicToken}
              </a>
              <div className="flex items-center gap-2 mt-1">
                <div
                  className={`w-2.5 h-2.5 rounded-full shrink-0 ${bridgeStatusDotClass(status)}`}
                  aria-hidden="true"
                />
                <span className="text-sm text-muted-foreground">{bridgeStatusLabel(status)}</span>
              </div>
            </div>
          </div>
          <BridgeActionsMenu
            bridge={bridge}
            status={status}
            isNativeBridge={isNativeBridge}
            isPod={isPod}
            publicTokenAddress={publicTokenAddress}
            chainId={chainId}
            openModal={openModal}
            setOperatorsModal={setOperatorsModal}
            navigate={navigate}
            accessStatus={accessStatus}
            feeSymbol={feeSymbol}
          />
        </div>

        <dl className="grid grid-cols-1 gap-3 text-sm">
          <div className="flex items-center justify-between gap-4 py-2 border-b border-border">
            <dt className="text-muted-foreground">Balance</dt>
            <dd className="font-mono text-foreground text-right">{balance}</dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-2 border-b border-border">
            <dt className="text-muted-foreground">Public Token</dt>
            <dd>
              {publicTokenAddress ? (
                <a href={getExplorerUrl(publicTokenAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1">
                  <Badge variant="outline" className="bg-primary/10">{bridge.publicToken}</Badge>
                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                </a>
              ) : (
                <Badge variant="outline" className="bg-primary/10">{bridge.publicToken}</Badge>
              )}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-2 border-b border-border">
            <dt className="text-muted-foreground">Private Token</dt>
            <dd>
              {privateTokenAddress ? (
                <a href={getExplorerUrl(privateTokenAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1">
                  <Badge variant="outline" className="bg-secondary/10">{bridge.privateToken}</Badge>
                  <ExternalLink className="h-3 w-3 text-muted-foreground" />
                </a>
              ) : (
                <Badge variant="outline" className="bg-secondary/10">{bridge.privateToken}</Badge>
              )}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-4 py-2">
            <dt className="text-muted-foreground">Accumulated {bridge.feeTokenSymbol || 'COTI'} Fees</dt>
            <dd className="font-mono text-foreground text-right">{fees}</dd>
          </div>
        </dl>

        <BridgeFeesDetail bridge={bridge} />
      </CardContent>
    </Card>
  );
};

const BridgeRow = ({ bridge, isPod, chainId, getExplorerUrl, publicTokenAddress, privateTokenAddress, openModal, setOperatorsModal, accessStatus, feeSymbol }: BridgeItemProps) => {
  const status = useBridgeStatus(bridge);
  // PoD native portals hold wrapped ERC-20 collateral — see BridgeCard comment.
  const isNativeBridge = isPod ? false : bridge.publicToken === 'COTI';
  const navigate = useNavigate();

  return (
    <TableRow className="hover:bg-muted/50">
      <TableCell className="font-medium min-w-0 whitespace-normal px-2 py-3">
        <div className="flex items-start gap-2 min-w-0">
          <img
            src={bridge.publicTokenIcon}
            alt={bridge.publicToken}
            className="w-6 h-6 rounded-full shrink-0"
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
          <div className="min-w-0">
            <a
              href={getExplorerUrl(bridge.bridgeAddress)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs bg-muted px-2 py-1 rounded hover:bg-muted/80 transition-colors inline-block max-w-full truncate"
            >
              <code className="text-primary hover:underline">{bridge.publicToken}</code>
            </a>
            <div className="mt-1.5">
              <BridgeFeesDetail bridge={bridge} />
            </div>
          </div>
        </div>
      </TableCell>
      <TableCell className="whitespace-normal px-2 py-3">
        <div className="flex items-center gap-2">
          <div
            className={`w-3 h-3 rounded-full shrink-0 ${bridgeStatusDotClass(status)}`}
            title={bridgeStatusLabel(status)}
          />
          <span className="text-sm text-muted-foreground">{bridgeStatusLabel(status)}</span>
        </div>
      </TableCell>
      <TableCell className="text-right font-mono text-xs whitespace-normal break-all px-2 py-3">
        {bridge.bridgeBalance !== 'Error' ? parseFloat(bridge.bridgeBalance).toFixed(6) : bridge.bridgeBalance}
      </TableCell>
      <TableCell className="whitespace-normal px-2 py-3">
        {publicTokenAddress ? (
          <a href={getExplorerUrl(publicTokenAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 max-w-full">
            <Badge variant="outline" className="bg-primary/10 cursor-pointer hover:bg-primary/20 truncate">{bridge.publicToken}</Badge>
            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
          </a>
        ) : (
          <Badge variant="outline" className="bg-primary/10">{bridge.publicToken}</Badge>
        )}
      </TableCell>
      <TableCell className="whitespace-normal px-2 py-3">
        {privateTokenAddress ? (
          <a href={getExplorerUrl(privateTokenAddress)} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 max-w-full">
            <Badge variant="outline" className="bg-secondary/10 cursor-pointer hover:bg-secondary/20 truncate">{bridge.privateToken}</Badge>
            <ExternalLink className="h-3 w-3 text-muted-foreground shrink-0" />
          </a>
        ) : (
          <Badge variant="outline" className="bg-secondary/10">{bridge.privateToken}</Badge>
        )}
      </TableCell>
      <TableCell className="text-right font-mono text-xs whitespace-normal break-all px-2 py-3">
        {bridge.accumulatedCotiFees !== 'Error' ? parseFloat(bridge.accumulatedCotiFees).toFixed(6) : bridge.accumulatedCotiFees}
      </TableCell>
      <TableCell className="px-2 py-3">
        <BridgeActionsMenu
          bridge={bridge}
          status={status}
          isNativeBridge={isNativeBridge}
          isPod={isPod}
          publicTokenAddress={publicTokenAddress}
          chainId={chainId}
          openModal={openModal}
          setOperatorsModal={setOperatorsModal}
          navigate={navigate}
          accessStatus={accessStatus}
          feeSymbol={feeSymbol}
        />
      </TableCell>
    </TableRow>
  );
};

const Dashboard = () => {
  const { chainId, walletAddress } = useCotiPluginContext();
  const networkChainId = chainId ? parseInt(chainId) : 7082400;
  const accessStatus = useDashboardAccess(walletAddress || null, networkChainId);
  const { bridgesData, isLoading, error, refresh } = useBridgeData(networkChainId);
  const chainCfg = getChainConfig(networkChainId);
  const isPod = chainCfg?.portalStrategy === 'pod-privacy-portal';
  const feeSymbol = isPod ? chainCfg!.walletNetwork.nativeCurrency.symbol : 'COTI';
  const {
    status: bandOracleStatus,
    isLoading: bandOracleLoading,
    error: bandOracleError,
    refresh: refreshBandOracle,
  } = useBandOracleStatus(isPod ? null : networkChainId);
  const {
    status: podOracleStatus,
    isLoading: podOracleLoading,
    error: podOracleError,
    refresh: refreshPodOracle,
  } = usePodOracleStatus(isPod ? networkChainId : null);
  
  // Add a delayed refresh callback to ensure on-chain data is updated
  const handleTransactionConfirmed = () => {
    // Immediate refresh
    refresh();
    // Additional refresh after 2 seconds to ensure blockchain state is updated
    setTimeout(() => {
      refresh();
    }, 2000);
  };
  
  const {
    setDepositFee,
    setWithdrawFee,
    podSetDepositFee,
    podSetWithdrawFee,
    podSetLimits,
    podPause,
    podUnpause,
    podSetDepositEnabled,
    podWithdrawPortalFees,
    podRescueNative,
    podRescueErc20,
    setLimits,
    rescueCotiFees,
    rescueNative,
    pauseContract,
    unpauseContract,
    setIsDepositEnabled,
  } = useBridgeAdmin(handleTransactionConfirmed);

  const [modalState, setModalState] = useState<{
    isOpen: boolean;
    actionType: AdminActionType | null;
    bridgeName: string;
    bridgeAddress: string;
    isNativeBridge: boolean;
    tokenDecimals: number;
    tokenSymbol: string;
    tokenAddress: string;
    currentFees?: CurrentFees;
  }>({
    isOpen: false,
    actionType: null,
    bridgeName: '',
    bridgeAddress: '',
    isNativeBridge: false,
    tokenDecimals: 18,
    tokenSymbol: '',
    tokenAddress: ''
  });

  const [operatorsModal, setOperatorsModal] = useState<{
    isOpen: boolean;
    bridgeName: string;
    bridgeAddress: string;
    isNativeBridge: boolean;
  }>({
    isOpen: false,
    bridgeName: '',
    bridgeAddress: '',
    isNativeBridge: false,
  });

  const getNetworkName = () => {
    switch (networkChainId) {
      case 7082400:
        return 'COTI Testnet';
      case 2632500:
        return 'COTI';
      default:
        return chainCfg?.name ?? 'Unknown Network';
    }
  };

  const getExplorerUrl = (address: string) => {
    if (isPod) {
      return `${getExplorerBaseUrlForChain(networkChainId)}/address/${address}`;
    }
    const baseUrl = networkChainId === 7082400
      ? 'https://testnet.cotiscan.io'
      : 'https://mainnet.cotiscan.io';
    return `${baseUrl}/address/${address}`;
  };

  const openModal = (
    actionType: AdminActionType,
    bridgeName: string,
    bridgeAddress: string,
    isNativeBridge: boolean,
    tokenDecimals: number = 18,
    tokenSymbol: string = '',
    tokenAddress: string = ''
  ) => {
    // Look up current on-chain fees/limits for this bridge
    const bridge = bridgesData.find(b => b.bridgeAddress === bridgeAddress);
    const currentFees: CurrentFees | undefined = bridge ? {
      depositFixedFee: bridge.depositFixedFee,
      depositPercentageBps: bridge.depositPercentageBps,
      depositMaxFee: bridge.depositMaxFee,
      withdrawFixedFee: bridge.withdrawFixedFee,
      withdrawPercentageBps: bridge.withdrawPercentageBps,
      withdrawMaxFee: bridge.withdrawMaxFee,
      minDepositAmount: bridge.minDepositAmount,
      maxDepositAmount: bridge.maxDepositAmount,
      minWithdrawAmount: bridge.minWithdrawAmount,
      maxWithdrawAmount: bridge.maxWithdrawAmount,
    } : undefined;

    setModalState({
      isOpen: true,
      actionType,
      bridgeName,
      bridgeAddress,
      isNativeBridge,
      tokenDecimals,
      tokenSymbol,
      tokenAddress,
      currentFees
    });
  };

  const closeModal = () => {
    setModalState({
      isOpen: false,
      actionType: null,
      bridgeName: '',
      bridgeAddress: '',
      isNativeBridge: false,
      tokenDecimals: 18,
      tokenSymbol: '',
      tokenAddress: ''
    });
  };

  const handleAdminAction = async (params: any) => {
    const { actionType, bridgeAddress, isNativeBridge, tokenDecimals } = modalState;
    
    // The refresh callback is now handled inside useBridgeAdmin hook
    // after transaction confirmation
    switch (actionType) {
      case 'setDepositFee':
        if (isPod) {
          await podSetDepositFee(networkChainId, bridgeAddress, params.fixedFee, params.percentageBps, params.maxFee);
        } else {
          await setDepositFee(bridgeAddress, params.fixedFee, params.percentageBps, params.maxFee, isNativeBridge);
        }
        break;
      case 'setWithdrawFee':
        if (isPod) {
          await podSetWithdrawFee(networkChainId, bridgeAddress, params.fixedFee, params.percentageBps, params.maxFee);
        } else {
          await setWithdrawFee(bridgeAddress, params.fixedFee, params.percentageBps, params.maxFee, isNativeBridge);
        }
        break;
      case 'setLimits':
        if (isPod) {
          await podSetLimits(
            networkChainId,
            bridgeAddress,
            params.minDeposit,
            params.maxDeposit,
            params.minWithdraw,
            params.maxWithdraw,
            tokenDecimals,
          );
        } else {
          await setLimits(bridgeAddress, params.minDeposit, params.maxDeposit, params.minWithdraw, params.maxWithdraw, isNativeBridge, tokenDecimals);
        }
        break;
      case 'rescueCoti':
        if (isPod) {
          await podWithdrawPortalFees(networkChainId, bridgeAddress, params.amount);
        } else {
          await rescueCotiFees(bridgeAddress, params.amount, isNativeBridge);
        }
        break;
      case 'rescueNative':
        if (isPod) {
          if (isNativeBridge) {
            await podRescueNative(networkChainId, bridgeAddress, params.amount, tokenDecimals);
          } else {
            if (!modalState.tokenAddress) {
              throw new Error('Token address is required for ERC20 portal rescue');
            }
            await podRescueErc20(
              networkChainId,
              bridgeAddress,
              modalState.tokenAddress,
              params.amount,
              tokenDecimals,
            );
          }
        } else {
          await rescueNative(
            bridgeAddress,
            params.amount,
            isNativeBridge,
            tokenDecimals,
            modalState.tokenAddress || undefined,
          );
        }
        break;
      case 'pause':
        if (isPod) {
          await podPause(networkChainId, bridgeAddress);
        } else {
          await pauseContract(bridgeAddress, isNativeBridge);
        }
        break;
      case 'unpause':
        if (isPod) {
          await podUnpause(networkChainId, bridgeAddress);
        } else {
          await unpauseContract(bridgeAddress, isNativeBridge);
        }
        break;
      case 'enableDeposits':
        if (isPod) {
          await podSetDepositEnabled(networkChainId, bridgeAddress, true);
        } else {
          await setIsDepositEnabled(bridgeAddress, true, isNativeBridge);
        }
        break;
      case 'disableDeposits':
        if (isPod) {
          await podSetDepositEnabled(networkChainId, bridgeAddress, false);
        } else {
          await setIsDepositEnabled(bridgeAddress, false, isNativeBridge);
        }
        break;
    }
  };

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar showAdminBadge />
      
      <ConnectWalletGuard
        title="Connect Wallet to Access Dashboard"
        description="Please connect your wallet to view bridge statistics and perform admin operations."
      >
        <main className="flex-1 container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto space-y-6">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-4xl font-bold text-glow mb-2">Backoffice</h1>
              <p className="text-muted-foreground">
                Monitor all privacy bridges and their fee structures on {getNetworkName()}
              </p>
            </div>
            
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="glass px-4 py-2 text-sm">
                {getNetworkName()}
              </Badge>
              
              <Button
                variant="outline"
                size="icon"
                onClick={() => { refresh(); void (isPod ? refreshPodOracle() : refreshBandOracle()); }}
                disabled={isLoading}
                className="glass"
                title="Refresh data"
              >
                <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
            {/* Card 1: Bridge Stats */}
            <Card className="glass box-glow">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Bridge Overview</CardTitle>
              </CardHeader>
              <div className="border-t border-border mx-6" />
              <CardContent className="pt-4">
                <div className="grid grid-cols-2 gap-y-3">
                  <span className="text-sm text-foreground">Total Bridges</span>
                  <span className="text-sm text-foreground text-right">
                    {isLoading ? <Skeleton className="h-4 w-8 ml-auto" /> : bridgesData.length}
                  </span>
                  <span className="text-sm text-foreground">Total {feeSymbol} Fees</span>
                  <span className="text-sm text-foreground text-right">
                    {isLoading ? <Skeleton className="h-4 w-16 ml-auto" /> : bridgesData.reduce((sum, bridge) => {
                      const fees = parseFloat(bridge.accumulatedCotiFees);
                      return sum + (isNaN(fees) ? 0 : fees);
                    }, 0).toFixed(4)}
                  </span>
                </div>
              </CardContent>
            </Card>


          </div>

          {/* Oracle Feeds — Band on COTI, PoDPriceOracle on Sepolia/Fuji */}
          <OracleStatusCard
            key={networkChainId}
            source={isPod ? 'pod' : 'band'}
            bandStatus={bandOracleStatus}
            podStatus={podOracleStatus}
            isLoading={isPod ? podOracleLoading : bandOracleLoading}
            error={isPod ? podOracleError : bandOracleError}
            getExplorerUrl={getExplorerUrl}
          />

          {/* Error Alert */}
          {error && (
            <Alert variant="destructive" className="glass">
              <AlertCircle className="h-4 w-4 shrink-0" />
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{formatAdminError(error, 'Failed to load bridge data')}</AlertDescription>
            </Alert>
          )}

          {/* Bridges Table */}
          <Card className="glass box-glow">
            <CardHeader>
              <CardTitle className="text-glow">Bridge Details</CardTitle>
              <CardDescription>
                Detailed information about each privacy bridge including fees and accumulated amounts
              </CardDescription>
            </CardHeader>
            <CardContent className="overflow-x-hidden">
              {isLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <Skeleton key={i} className="h-16 w-full" />
                  ))}
                </div>
              ) : bridgesData.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  No bridges found for this network
                </div>
              ) : (
                <>
                  {/* Mobile / tablet: card layout */}
                  <div className="xl:hidden space-y-4">
                    {bridgesData.map((bridge, index) => {
                      const tokenSource = isPod ? chainCfg!.tokens : SUPPORTED_TOKENS;
                      const addresses = isPod ? chainCfg!.addresses : CONTRACT_ADDRESSES[networkChainId as keyof typeof CONTRACT_ADDRESSES];
                      const pubToken = tokenSource.find(t => !t.isPrivate && t.symbol === bridge.publicToken);
                      const privToken = tokenSource.find(t => t.isPrivate && t.bridgeAddressKey === pubToken?.bridgeAddressKey);
                      const pubAddr = pubToken?.addressKey && addresses ? (addresses as any)[pubToken.addressKey] || null : null;
                      const privAddr = privToken?.addressKey && addresses ? (addresses as any)[privToken.addressKey] || null : null;

                      return (
                        <BridgeCard
                          key={index}
                          bridge={bridge}
                          isPod={isPod}
                          chainId={networkChainId}
                          getExplorerUrl={getExplorerUrl}
                          publicTokenAddress={pubAddr}
                          privateTokenAddress={privAddr}
                          openModal={openModal}
                          setOperatorsModal={setOperatorsModal}
                          accessStatus={accessStatus}
                          feeSymbol={feeSymbol}
                        />
                      );
                    })}
                  </div>

                  {/* Desktop: table layout */}
                  <div className="hidden xl:block overflow-x-hidden">
                    <Table className="table-fixed w-full">
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-[28%] px-2">Bridge</TableHead>
                          <TableHead className="w-[8%] px-2">Status</TableHead>
                          <TableHead className="w-[12%] text-right px-2">Balance</TableHead>
                          <TableHead className="w-[12%] px-2">Public Token</TableHead>
                          <TableHead className="w-[12%] px-2">Private Token</TableHead>
                          <TableHead className="w-[14%] text-right px-2">{feeSymbol} Fees</TableHead>
                          <TableHead className="w-[14%] px-2">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bridgesData.map((bridge, index) => {
                          const tokenSource = isPod ? chainCfg!.tokens : SUPPORTED_TOKENS;
                          const addresses = isPod ? chainCfg!.addresses : CONTRACT_ADDRESSES[networkChainId as keyof typeof CONTRACT_ADDRESSES];
                          const pubToken = tokenSource.find(t => !t.isPrivate && t.symbol === bridge.publicToken);
                          const privToken = tokenSource.find(t => t.isPrivate && t.bridgeAddressKey === pubToken?.bridgeAddressKey);
                          const pubAddr = pubToken?.addressKey && addresses ? (addresses as any)[pubToken.addressKey] || null : null;
                          const privAddr = privToken?.addressKey && addresses ? (addresses as any)[privToken.addressKey] || null : null;

                          return (
                            <BridgeRow
                              key={index}
                              bridge={bridge}
                              isPod={isPod}
                              chainId={networkChainId}
                              getExplorerUrl={getExplorerUrl}
                              publicTokenAddress={pubAddr}
                              privateTokenAddress={privAddr}
                              openModal={openModal}
                              setOperatorsModal={setOperatorsModal}
                              accessStatus={accessStatus}
                              feeSymbol={feeSymbol}
                            />
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </main>
      </ConnectWalletGuard>

      <Footer />

      {/* Admin Action Modal */}
      {modalState.actionType && (
        <AdminActionModal
          isOpen={modalState.isOpen}
          onClose={closeModal}
          actionType={modalState.actionType}
          bridgeName={modalState.bridgeName}
          bridgeAddress={modalState.bridgeAddress}
          tokenSymbol={modalState.tokenSymbol}
          tokenAddress={modalState.tokenAddress}
          tokenDecimals={modalState.tokenDecimals}
          currentFees={modalState.currentFees}
          chainId={networkChainId}
          isPod={isPod}
          feeSymbol={feeSymbol}
          isNativeBridge={modalState.isNativeBridge}
          onExecute={handleAdminAction}
        />
      )}

      {/* Operators Modal */}
      <OperatorsModal
        isOpen={operatorsModal.isOpen}
        onClose={() => setOperatorsModal(s => ({ ...s, isOpen: false }))}
        bridgeName={operatorsModal.bridgeName}
        bridgeAddress={operatorsModal.bridgeAddress}
        isNativeBridge={operatorsModal.isNativeBridge}
        networkChainId={networkChainId}
      />
    </div>
  );
};

export default Dashboard;

// Made with Bob
